/**
 * Virtual runs: a chart of somebody else's trades, built entirely from the URL.
 *
 * An external tool that decides trades but does not store them still needs a way to show one on a
 * chart. Rather than have it write rows into the database, it encodes the trades into a link. The
 * page decodes that, builds the same RunResult shape a real backtest produces, and renders the
 * ordinary chart with ordinary entry and exit markers. Nothing is persisted and nothing is faked
 * downstream: the chart cannot tell the difference.
 *
 * The payload is base64url of compact JSON, so a link can be pasted into any decoder and read.
 * That is deliberate. An opaque blob in a URL is impossible to debug when a chart looks wrong.
 */

import type * as T from "@src/types";

/** One trade to draw. Dates are ISO; an open trade simply omits the exit. */
export interface VirtualPosition {
  /** "long" or "short". */
  dir: "long" | "short";
  /** Entry date or timestamp, ISO. */
  in: string;
  /** Entry price. */
  inPx: number;
  /** Exit date or timestamp, ISO. Omitted while the trade is open. */
  out?: string;
  /** Exit price. Omitted while the trade is open. */
  outPx?: number;
  /** Position size in base units. Only affects the size shown in the marker tooltip. */
  size?: number;
  /** Stop price, drawn so the risk on the trade is visible. */
  stop?: number;
  /** Realised profit as a percentage, shown on the exit marker. */
  pnlPct?: number;
  /** Realised profit in quote currency. */
  pnl?: number;
  /** Free text for why it closed, e.g. "trail" or "stop". */
  reason?: string;
  /** The close explained in a sentence, shown beside the reason. */
  note?: string;
}

/**
 * One series to draw beside the trades, in the compact form the link carries it.
 *
 * The whole payload rides in a URL, so the wire form is not the shape the chart wants. A series of
 * points spelled out as {"time":...,"value":...} costs about 35 bytes each, and a page of sixty
 * trades each carrying five of those is megabytes of link.
 *
 * There are two forms, and which one a series takes is about correctness before it is about size.
 * A reading taken every day is REGULAR: describe the grid once with `t0` and `dt`, send only the
 * values. A level that changes when something happens is not, and forcing it onto a grid moves the
 * change to the nearest grid point, which is a lie about when it happened; that one carries `t`,
 * an explicit time per value. It is also shorter, because a flat level is two points rather than
 * one a day.
 *
 * Keys are terse throughout, for the size reason. This is the one place in the codebase where that
 * is worth it: every character here is multiplied by the number of points on the page.
 */
export interface VirtualIndicator {
  /** Legend name. "Foo Upper" and "Foo Lower" are paired into a filled band by the chart. */
  n: string;
  /** "main" draws it over price, "pane" gives it its own panel below. Defaults to main. */
  p?: "main" | "pane";
  /** Hex colour, e.g. "#f85149". */
  c?: string;
  /** Line style. Defaults to solid. */
  ls?: "solid" | "dotted" | "dashed";
  /** Plot style. Defaults to a line; a series with gaps is switched to linewithbreaks for you. */
  pt?: T.BaseIndicator["style"]["plotype"];
  /** Line width in pixels. Defaults to 1. */
  lw?: number;
  /** 0 is opaque, 100 invisible. Defaults to 0, since a caller who sends a series wants to see it. */
  tr?: number;
  /**
   * Take the value from the bar rather than from `v`, so a band edge can BE the price. The times
   * still bound where it is drawn; only the value comes from the chart. Sending the price as data
   * instead would mean shipping a close for every bar, at a resolution the viewer picks and the
   * sender cannot know.
   *
   * Only an edge of a PAIR reads this. On a series with no counterpart it is ignored and the
   * series draws its own values, because a lone line that redraws the close is a line on top of
   * the price and has nothing to say.
   */
  src?: "close";
  /**
   * Timestamp of the first value, in SECONDS. Seconds, not milliseconds: that is what the chart
   * uses. Present on a REGULAR series, together with `dt`; absent on an irregular one, which
   * carries `t` instead.
   */
  t0?: number;
  /** Seconds between values on a regular series. 86400 for a daily one. */
  dt?: number;
  /**
   * Explicit timestamps in SECONDS, one per value, oldest first. The alternative to `t0` + `dt`,
   * for a series whose points do not sit on a grid.
   *
   * A level that only changes when something happens -- a stop the trail ratchets, a threshold that
   * holds from an entry to an exit -- has nothing to say on the days in between, and a grid makes
   * it say it anyway. Worse, a grid can only place a change on a grid point, so a stop that moved
   * at two in the afternoon gets drawn from midnight: below where it really was, for hours, which
   * makes it look as though price touched a level that did not yet exist. Explicit times put each
   * change at the moment it happened, and a flat level costs two points instead of one a day.
   */
  t?: number[];
  /**
   * The values, oldest first. null is a hole rather than a zero: it becomes NaN, which the chart
   * draws as a break in the line. That is what lets one series cover a window it is only defined
   * for part of -- a stop that exists between an entry and an exit and nowhere else.
   */
  v: (number | null)[];
}

export interface VirtualRunPayload {
  /** Payload version, so an old link can be rejected rather than misread. */
  v: 1;
  exchange: string;
  /** Platform symbol name, e.g. "KAITOUSDT_PERP". */
  symbol: string;
  /** Candle period, e.g. "4h". Defaults to 5m, the timeframe the live strategies trade on. */
  period?: string;
  /** Window to open the chart on, ISO dates. */
  from?: string;
  to?: string;
  /** Book size, only used to label the trades. */
  capital?: number;
  /** What to draw. */
  positions: VirtualPosition[];
  /** Shown above the chart, e.g. "Pump fade (paper)". */
  title?: string;
  /** Market the symbol trades on. Decides whether the chart expects weekend candles. */
  kind?: T.TDT.MarketKind;
  /** Series to draw alongside the trades, so the link can show WHY the trade happened. */
  indicators?: VirtualIndicator[];
}

/** Movement action ids the chart uses to tell an opening leg from a closing one. */
const OPEN_FUTURE = 3;
const CLOSE_FUTURE = 4;

// btoa and atob work on bytes, not text, so anything above Latin-1 in a title or an exit reason
// has to go through UTF-8 first or the encode throws and a round trip mangles the characters.
const toBase64 = (text: string): string =>
  btoa(String.fromCharCode(...new TextEncoder().encode(text)));

const fromBase64 = (b64: string): string =>
  new TextDecoder().decode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)));

export const encodeVirtualRun = (payload: VirtualRunPayload): string =>
  toBase64(JSON.stringify(payload)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/** Decode a link payload, or throw with a reason a human can act on. */
export const decodeVirtualRun = (encoded: string): VirtualRunPayload => {
  let json: string;
  try {
    const padded = encoded.replace(/-/g, "+").replace(/_/g, "/");
    json = fromBase64(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  } catch {
    throw new Error("This link is not valid base64url.");
  }

  let payload: VirtualRunPayload;
  try {
    payload = JSON.parse(json) as VirtualRunPayload;
  } catch {
    throw new Error("This link does not contain valid JSON.");
  }

  if (payload.v !== 1) {
    throw new Error(`Unsupported link version ${String(payload.v)}. This page understands v1.`);
  }
  if (!payload.exchange || !payload.symbol) {
    throw new Error("The link is missing an exchange or a symbol.");
  }
  if (!Array.isArray(payload.positions)) {
    throw new Error("The link is missing its positions array.");
  }
  // Every position is checked here rather than where it is drawn. A link is external input, and a
  // bad date only fails once something calls toISOString on it, which happens during render where
  // there is no catch left to turn it into a message.
  payload.positions.forEach(validatePosition);
  if (payload.indicators !== undefined) {
    if (!Array.isArray(payload.indicators)) {
      throw new Error("The link's indicators are not an array.");
    }
    payload.indicators.forEach(validateIndicator);
  }
  return payload;
};

const isDate = (value: unknown): boolean =>
  typeof value === "string" && !Number.isNaN(new Date(value).getTime());

const validatePosition = (p: VirtualPosition, i: number): void => {
  const at = `Position ${i + 1}`;
  if (!p || typeof p !== "object") {
    throw new Error(`${at} is not an object.`);
  }
  if (p.dir !== "long" && p.dir !== "short") {
    throw new Error(`${at} has direction "${String(p.dir)}". Expected "long" or "short".`);
  }
  if (!isDate(p.in)) {
    throw new Error(`${at} has an entry date this page cannot read: "${String(p.in)}".`);
  }
  if (!Number.isFinite(p.inPx)) {
    throw new Error(`${at} has no usable entry price.`);
  }
  if (p.out !== undefined && !isDate(p.out)) {
    throw new Error(`${at} has an exit date this page cannot read: "${String(p.out)}".`);
  }
  if (p.outPx !== undefined && !Number.isFinite(p.outPx)) {
    throw new Error(`${at} has no usable exit price.`);
  }
  if (Boolean(p.out) !== (p.outPx !== undefined)) {
    throw new Error(`${at} is half closed. An exit needs both its date and its price.`);
  }
};

/**
 * Whether a number can survive the trip to the chart as a timestamp.
 *
 * Times end up in a Uint32Array, so anything fractional is truncated and anything past 2^32-1 wraps
 * around to a different date entirely. Neither throws. A link that sends milliseconds by mistake --
 * a plausible mistake, since exchanges and the engine both work in them -- would silently draw its
 * series in the wrong decade rather than fail, which is the hardest kind of wrong to notice.
 */
const isChartTime = (value: unknown): boolean =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 4_294_967_295;

const validateIndicator = (ind: VirtualIndicator, i: number): void => {
  const at = `Indicator ${i + 1}`;
  if (!ind || typeof ind !== "object") {
    throw new Error(`${at} is not an object.`);
  }
  if (typeof ind.n !== "string" || !ind.n) {
    throw new Error(`${at} has no name.`);
  }
  if (!Array.isArray(ind.v) || ind.v.length === 0) {
    // Empty is rejected rather than tolerated. An indicator with no values reaches the chart as an
    // empty typed array, and the study reads that as the number zero rather than as nothing, so it
    // draws a flat line along the bottom of the chart for the whole window. A caller with nothing
    // to say should send no series.
    throw new Error(`${at} ("${ind.n}") has no values.`);
  }
  // Timing is the part that cannot be recovered if it is wrong. A bad t0, dt or t throws nowhere
  // downstream, it silently places the series in the wrong century, so it is checked here.
  const irregular = ind.t !== undefined;
  if (irregular === (ind.t0 !== undefined || ind.dt !== undefined)) {
    throw new Error(
      `${at} ("${ind.n}") must carry either t0 and dt for a regular series, or t for an irregular one.`,
    );
  }
  if (irregular) {
    const times = ind.t as number[];
    if (!Array.isArray(times) || times.length !== ind.v.length) {
      throw new Error(
        `${at} ("${ind.n}") has ${times?.length ?? 0} times for ${ind.v.length} values.`,
      );
    }
    const outOfOrder = times.findIndex(
      (time, n) => !isChartTime(time) || (n > 0 && time < times[n - 1]),
    );
    if (outOfOrder >= 0) {
      throw new Error(
        `${at} ("${ind.n}") has a time at ${outOfOrder} that is unreadable or out of order.`,
      );
    }
  } else {
    if (!isChartTime(ind.t0)) {
      throw new Error(`${at} ("${ind.n}") has no usable start time.`);
    }
    if (!Number.isInteger(ind.dt) || (ind.dt as number) <= 0) {
      throw new Error(
        `${at} ("${ind.n}") has a step of "${String(ind.dt)}". Expected whole seconds above zero.`,
      );
    }
    // The far end as well as the near one, because the grid is expanded rather than sent, so a
    // plausible start with a large step still lands the last point outside the range.
    if (!isChartTime((ind.t0 as number) + (ind.v.length - 1) * (ind.dt as number))) {
      throw new Error(`${at} ("${ind.n}") runs past the end of time the chart can hold.`);
    }
  }
  const bad = ind.v.findIndex((value) => value !== null && !Number.isFinite(value));
  if (bad >= 0) {
    throw new Error(
      `${at} ("${ind.n}") has a value at ${bad} this page cannot read. Use null for a gap.`,
    );
  }
};

/**
 * Expand one wire series back into the points the chart draws from.
 *
 * A hole becomes NaN rather than being dropped. Dropping it would leave the chart's lookup, which
 * takes the newest point at or before each candle, carrying the last real value forward across the
 * gap and out to the right edge -- so a stop that ended in August would still be drawn today. NaN
 * is read as "nothing here" and breaks the line where the series really stops.
 */
const toIndicator = (ind: VirtualIndicator): T.SerialLineIndicator => ({
  name: ind.n,
  // "chart" is what the chart calls it internally; the wire says "main" to match the word the
  // backend's own chart definitions use, so the two describe the same thing the same way.
  position: ind.p === "pane" ? "pane" : "chart",
  type: "line",
  ...(ind.src ? { source: ind.src } : {}),
  style: {
    linestyle: ind.ls ?? "solid",
    plotype: ind.pt ?? "line",
    linewidth: ind.lw ?? 1,
    // Zero, not the 40 a chart-definition indicator defaults to. A caller who went to the trouble
    // of encoding a series into a URL meant it to be read, not faded.
    transparency: ind.tr ?? 0,
    color: ind.c ?? "#888888",
  },
  data: ind.v.map((value, i) => ({
    time: ind.t ? ind.t[i] : (ind.t0 as number) + i * (ind.dt as number),
    value: value ?? NaN,
  })),
});

/**
 * Split a contract name into its traded asset and the currency it is priced in.
 *
 * Names arrive as "KAITOUSDT_PERP" or "BTCUSDC". The quote is whichever known currency the name
 * ends with once any contract suffix is dropped; everything before it is the base.
 */
const QUOTES = ["USDT", "USDC", "USD", "BTC", "ETH", "EUR"];

export const splitContract = (name: string): { base: string; quote: string } => {
  const bare = name.replace(/_(PERP|SWAP|FUTURES)$/i, "");
  const quote = QUOTES.find((q) => bare.endsWith(q));
  return quote ? { base: bare.slice(0, -quote.length), quote } : { base: bare, quote: "" };
};

const iso = (value: string): string =>
  value.length === 10 ? `${value}T00:00:00Z` : new Date(value).toISOString();

const movement = (
  id: number,
  positionId: number,
  actionId: number,
  ts: string,
  price: number,
  amount: number,
  reason?: string,
  note?: string,
): T.ApiMovement => ({
  id,
  position_id: positionId,
  action_id: actionId,
  amount,
  price,
  // A virtual run knows the trade, not its execution detail. Zeroes here are honest: the marker
  // tooltip shows no fee or slippage rather than inventing one.
  fees: 0,
  fees_currency: "USDT",
  fees_usdt: 0,
  ordered_amount: amount,
  expected_price: price,
  expected_slippage: 0,
  slippage: 0,
  spread: 0,
  last_price: price,
  movement_ts: iso(ts),
  create_ts: iso(ts),
  reason_key: reason ?? null,
  reason_note: note ?? null,
});

/**
 * Turn one decoded trade into the position shape the chart draws from.
 *
 * Ids are synthetic and negative so nothing can mistake them for rows that exist. The chart keys
 * markers by movement id, so they only have to be unique within the page.
 */
const toPosition = (
  p: VirtualPosition,
  index: number,
  payload: VirtualRunPayload,
): T.AppPosition => {
  const id = -(index + 1);
  const amount = p.size ?? 1;
  const closed = Boolean(p.out);

  const movements: T.ApiMovement[] = [movement(id * 10, id, OPEN_FUTURE, p.in, p.inPx, amount)];
  if (closed && p.outPx !== undefined && p.out) {
    movements.push(
      movement(id * 10 - 1, id, CLOSE_FUTURE, p.out, p.outPx, amount, p.reason, p.note),
    );
  }

  return {
    id,
    type_name: "futures",
    direction_name: p.dir,
    symbol_name: payload.symbol,
    exchange_name: payload.exchange,
    status_name: closed ? "closed" : "active",
    entry_price: p.inPx,
    exit_price: p.outPx ?? null,
    entry_amount: amount,
    current_amount: closed ? 0 : amount,
    start_ts: iso(p.in),
    end_ts: p.out ? iso(p.out) : null,
    create_ts: iso(p.in),
    update_ts: iso(p.out ?? p.in),
    leverage: 1,
    collateral_in: p.inPx * amount,
    // The link carries profit in the quote currency. Copying it into the USDT field would relabel a
    // EUR number as dollars, so only a USDT contract fills both.
    net_profit_usdt: splitContract(payload.symbol).quote === "USDT" ? (p.pnl ?? null) : null,
    net_profit_quote: p.pnl ?? null,
    net_profit_pct: p.pnlPct ?? null,
    movements,
    stop_loss: p.stop ?? null,
    take_profit: null,
    tv_shape_id: null,
  } as unknown as T.AppPosition;
};

/**
 * Build the RunResult the chart consumes.
 *
 * Candles are left empty on purpose: the datafeed's live-fetch mode pulls them for the symbol and
 * resolution, which is what lets this work for any contract rather than only the ones the platform
 * already stores.
 *
 * Indicators are whatever the caller sent and usually nothing. A run read from the database has no
 * way to produce them here, but a link does: the tool that decided the trade knows what it was
 * looking at, and a chart of an entry with no sign of the level that triggered it can only be read
 * by someone who already knows the rule. The lightweight series stay empty either way -- they plot
 * an equity curve, and one trade is not one.
 */
export interface PositionRunOptions {
  positions: T.AppPosition[];
  symbol: string;
  exchange: string;
  period?: string;
  capital?: number;
  kind?: T.TDT.MarketKind;
  from?: string;
  to?: string;
  indicators?: T.SerialLineIndicator[];
}

/**
 * The RunResult behind a chart of positions, wherever the positions came from.
 *
 * A position drawn from a link and a position read from the database are the same picture, so they
 * are the same shape by the time the chart sees them. Only the source differs.
 */
export const runResultForPositions = (opts: PositionRunOptions): T.RunResult =>
  ({
    // The strategies here trade on 5m candles, so that is the honest default when a caller does not
    // say. A daily candle can swallow an entry and its exit whole and show neither.
    api_period: opts.period ?? "5m",
    candles: [],
    indicators: opts.indicators ?? [],
    series: [],
    positions: opts.positions,
    initial_capital: opts.capital ?? 1000,
    metrics: undefined,
    // The chart legend reads base and quote, so a symbol without them renders
    // "undefined/undefined". Split the contract name rather than ask the caller to carry it.
    symbol: {
      name: opts.symbol,
      exchange: opts.exchange,
      base: splitContract(opts.symbol).base,
      quote: splitContract(opts.symbol).quote,
      // Not the contract type: this is the market, and the chart reads it to decide whether to
      // expect weekend candles. Anything but "crypto" gets a Mon-Fri session and hides them.
      kind: opts.kind ?? "crypto",
    },
    start_ts: opts.from ? iso(opts.from) : undefined,
    end_ts: opts.to ? iso(opts.to) : undefined,
  }) as unknown as T.RunResult;

export const buildVirtualRunResult = (payload: VirtualRunPayload): T.RunResult =>
  runResultForPositions({
    positions: payload.positions.map((p, i) => toPosition(p, i, payload)),
    symbol: payload.symbol,
    exchange: payload.exchange,
    period: payload.period,
    capital: payload.capital,
    kind: payload.kind,
    from: payload.from,
    to: payload.to,
    indicators: payload.indicators?.map(toIndicator),
  });
